/**
 * SDKConfigReader — loads sdkconfig.json and sdkconfig.schema.json from public/
 * and exposes helper functions that drive the ConfigPanel UI from the schema.
 *
 * Platform is always "web", sdk is always "web" for the CTV test app.
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SDKConfig {
  auto_configure: boolean;
  connection: {
    nami_host: string;
    app_platform_id: string;
    custom_host: string;
    custom_app_platform_id: string;
  };
  sdk_settings: {
    log_level: string;
    language_code: string;
    initial_config_mode: string;
    auto_launch_placement: string;
    form_factor: string;
  };
  debug: Record<string, boolean>;
  testing: {
    split_position_override: number | null;
    campaign_cache_ttl: number | null;
  };
}

export interface HostOption {
  value: string;
  label: string;
}

export interface PlatformOption {
  label: string;
  value: string;
  platform: string;
  sdk: string;
}

export interface DebugFlag {
  key: string;
  label: string;
  default: boolean;
}

// Raw schema types (enough to extract x-ui-* extensions)
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type SchemaJSON = Record<string, any>;

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const PLATFORM = 'web';
const SDK = 'web';

// ---------------------------------------------------------------------------
// Module-level cache
// ---------------------------------------------------------------------------

let cachedConfig: SDKConfig | null = null;
let cachedSchema: SchemaJSON | null = null;

// ---------------------------------------------------------------------------
// Loaders
// ---------------------------------------------------------------------------

async function fetchJSON<T>(path: string): Promise<T> {
  const res = await fetch(path, { cache: 'no-cache' });
  if (!res.ok) {
    throw new Error(`Failed to fetch ${path}: ${res.status} ${res.statusText}`);
  }
  return res.json() as Promise<T>;
}

export async function loadSDKConfig(): Promise<SDKConfig> {
  if (cachedConfig) return cachedConfig;
  cachedConfig = await fetchJSON<SDKConfig>('/sdkconfig.json');
  return cachedConfig;
}

export async function loadSchema(): Promise<SchemaJSON> {
  if (cachedSchema) return cachedSchema;
  cachedSchema = await fetchJSON<SchemaJSON>('/sdkconfig.schema.json');
  return cachedSchema;
}

// ---------------------------------------------------------------------------
// Schema helpers
// ---------------------------------------------------------------------------

function getDefProperty(schema: SchemaJSON, defName: string, propName: string): SchemaJSON | undefined {
  return schema?.['$defs']?.[defName]?.properties?.[propName];
}

// ---------------------------------------------------------------------------
// Host options (from connection.nami_host.oneOf)
// ---------------------------------------------------------------------------

export async function getHostOptions(): Promise<HostOption[]> {
  const schema = await loadSchema();
  const hostProp = getDefProperty(schema, 'connection', 'nami_host');
  if (!hostProp?.oneOf) return [];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return hostProp.oneOf.map((entry: any) => ({
    value: entry.const as string,
    label: (entry.title ?? entry.const) as string,
  }));
}

// ---------------------------------------------------------------------------
// Platform options (app_platform_id presets for a given host, filtered to web)
// ---------------------------------------------------------------------------

export async function getPlatformOptions(host: string): Promise<PlatformOption[]> {
  const schema = await loadSchema();
  const appPlatformProp = getDefProperty(schema, 'connection', 'app_platform_id');
  const mapping = appPlatformProp?.['x-ui-depends-on']?.mapping;
  if (!mapping || !mapping[host]) return [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return (mapping[host] as any[]).filter(
    (entry) => entry.platform === PLATFORM && (!entry.sdk || entry.sdk === SDK),
  );
}

// ---------------------------------------------------------------------------
// Log level options (from sdk_settings.log_level.enum)
// ---------------------------------------------------------------------------

export async function getLogLevelOptions(): Promise<string[]> {
  const schema = await loadSchema();
  const prop = getDefProperty(schema, 'sdk_settings', 'log_level');
  return (prop?.enum as string[]) ?? [];
}

// ---------------------------------------------------------------------------
// Language options (from sdk_settings.language_code.enum)
// ---------------------------------------------------------------------------

export async function getLanguageOptions(): Promise<string[]> {
  const schema = await loadSchema();
  const prop = getDefProperty(schema, 'sdk_settings', 'language_code');
  return (prop?.enum as string[]) ?? [];
}

// ---------------------------------------------------------------------------
// Form factor options (from sdk_settings.form_factor.enum)
// ---------------------------------------------------------------------------

export async function getFormFactorOptions(): Promise<string[]> {
  const schema = await loadSchema();
  const prop = getDefProperty(schema, 'sdk_settings', 'form_factor');
  return (prop?.enum as string[]) ?? [];
}

export async function getInitialConfigModeOptions(): Promise<string[]> {
  const schema = await loadSchema();
  const prop = getDefProperty(schema, 'sdk_settings', 'initial_config_mode');
  return (prop?.enum as string[]) ?? [];
}

// ---------------------------------------------------------------------------
// Debug flags (filtered to web platform + optional form factor)
// ---------------------------------------------------------------------------

export async function getDebugFlags(
  platform: string = PLATFORM,
  formFactor?: string,
): Promise<DebugFlag[]> {
  const schema = await loadSchema();
  const debugDef = schema?.['$defs']?.debug?.properties;
  if (!debugDef) return [];

  const flags: DebugFlag[] = [];
  for (const [key, prop] of Object.entries<SchemaJSON>(debugDef)) {
    const platforms: string[] | undefined = prop['x-ui-platforms'];
    // If x-ui-platforms is specified, the flag is only for those platforms
    if (platforms && !platforms.includes(platform)) continue;

    const formFactors: string[] | undefined = prop['x-ui-form-factors'];
    if (formFactors && formFactor && !formFactors.includes(formFactor)) continue;

    const label = key
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
      .join(' ');

    flags.push({
      key,
      label,
      default: prop.default ?? false,
    });
  }

  return flags;
}

// ---------------------------------------------------------------------------
// resolveImplies — returns nami_commands implied by a host selection
// ---------------------------------------------------------------------------

export async function resolveImplies(host: string): Promise<string[]> {
  const schema = await loadSchema();
  const hostProp = getDefProperty(schema, 'connection', 'nami_host');
  if (!hostProp?.oneOf) return [];

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const entry = hostProp.oneOf.find((e: any) => e.const === host);
  if (!entry) return [];

  const implies = entry['x-ui-implies'];
  if (!implies?.nami_commands) return [];
  return implies.nami_commands as string[];
}

// ---------------------------------------------------------------------------
// getHostURL — returns the URL for a host from x-ui-host-urls
// ---------------------------------------------------------------------------

export async function getHostURL(host: string): Promise<string | undefined> {
  const schema = await loadSchema();
  const hostProp = getDefProperty(schema, 'connection', 'nami_host');
  const urls = hostProp?.['x-ui-host-urls'];
  if (!urls) return undefined;
  return urls[host] as string | undefined;
}

// ---------------------------------------------------------------------------
// getCampaignCacheTTLPresets — returns the preset values from schema
// ---------------------------------------------------------------------------

export async function getCampaignCacheTTLPresets(): Promise<number[]> {
  const schema = await loadSchema();
  const prop = getDefProperty(schema, 'testing', 'campaign_cache_ttl');
  return (prop?.['x-ui-presets'] as number[]) ?? [];
}

// ---------------------------------------------------------------------------
// getCustomHostDependency — returns which host values trigger the custom host field
// ---------------------------------------------------------------------------

export async function getCustomHostDependency(): Promise<string[]> {
  const schema = await loadSchema();
  const prop = getDefProperty(schema, 'connection', 'custom_host');
  return (prop?.['x-ui-depends-on']?.values as string[]) ?? [];
}

// ---------------------------------------------------------------------------
// getCustomAppPlatformIdDependency — host values that require custom app platform id
// ---------------------------------------------------------------------------

export async function getCustomAppPlatformIdDependency(): Promise<string[]> {
  const schema = await loadSchema();
  const prop = getDefProperty(schema, 'connection', 'custom_app_platform_id');
  return (prop?.['x-ui-depends-on']?.values as string[]) ?? [];
}
